import { FormStep } from '../types'
import { DoorsIcon, HorizontalPodIcon, LiftDoorsIcon, VerticalPodIcon } from './icons'

export default [
  {
    type: 'content',
    title: 'Information',
    content: `This simple calculator has been created to provide an estimate of the extract rate required for a MSVS (Mechanical Smoke Ventilation System) with one mechanical extract shaft.<br><br> For more complex systems, please use our live chat and we'll help you out!`,
    button: 'Continue'
  },
  {
    title: 'Smoke shaft dimensions',
    text: 'Enter the shaft width and shaft depth of the smoke shaft.',
    video: 'https://a.storyblok.com/f/79904/x/3a12f76e32/shaft-dimensions-compressed.m4v',
    type: 'input',
    key: 'dimensions',
    button: 'Next',
    tip: {
      title: 'Why we need the shaft size.',
      text: 'The shaft size allows us to estimate the system static pressure and smoke shaft leakage.'
    },
    inputs: [
      {
        key: 'shaftWidth',
        type: 'number',
        placeholder: 'Enter Width (mm)',
        required: true,
        step: 1
      },
      {
        key: 'shaftDepth',
        type: 'number',
        placeholder: 'Enter Depth (mm)',
        required: true,
        step: 1
      }
    ],
    validations: [
      (data: Record<string, string | number>): boolean | string | undefined => {
        const { shaftWidth, shaftDepth } = data
        if (!shaftWidth || !shaftDepth) return
        // Formula: (W * D) / 1,000,000 (to get m2)
        const crossSectionalArea = (Number(shaftWidth) * Number(shaftDepth)) / 1000000
        const lessThan = crossSectionalArea < 0.6
        return (
          lessThan &&
          `
          <p>⚠️ These dimensions have a cross-sectional area of less than 0.6m², which will result in noisier, more expensive fan sets that are less energy efficient.</p>
          <p><strong>For the best results, increase the shaft size to 0.6m².</strong></p>
        `
        )
      },
      (data: Record<string, string | number>): boolean | string | undefined => {
        const { shaftWidth, shaftDepth } = data
        if (!shaftWidth || !shaftDepth) return
        // Formula: W / D = AR (expressed as a decimal)
        const aspectRatio1 = Number(shaftWidth) / Number(shaftDepth)
        const aspectRatio2 = Number(shaftDepth) / Number(shaftWidth)
        const greaterThan = aspectRatio1 > 2 || aspectRatio2 > 2

        return (
          greaterThan &&
          `
          <p>⚠️ The aspect ratio of these dimensions is greater than 2:1, which will result in noisier, more expensive fan sets that are less energy efficient.</p>
          <p><strong>For the best results, decrease the shaft aspect ratio.</strong></p>
        `
        )
      },
      (data: Record<string, string | number>): boolean | string | undefined => {
        const values = Object.values(data).filter(Boolean)
        const hasSmallerThan = values.some((val) => Number(val) < 600)
        return (
          hasSmallerThan &&
          `
          <p>
            <svg width="18" height="18" viewBox="0 0 72 72" style="position:relative;top:3px;fill-rule:evenodd;clip-rule:evenodd;stroke-linejoin:round;stroke-miterlimit:10;">
              <g id="line">
                <g transform="matrix(1.33333,0,0,1.33333,-12,-12)">
                  <path d="M59.035,60L12.965,60C12.434,60 12,59.566 12,59.035L12,12.965C12,12.434 12.434,12 12.965,12L59.035,12C59.566,12 60,12.434 60,12.965L60,59.035C60,59.566 59.566,60 59.035,60Z" style="fill:rgb(0,181,255);fill-rule:nonzero;stroke:rgb(0,181,255);stroke-width:2px;"/>
                </g>
                <g transform="matrix(1.33333,0,0,1.6,-12,-17.7333)">
                  <path d="M36.5,31.5L36.5,46.5" style="fill:none;fill-rule:nonzero;stroke:white;stroke-width:8.02px;stroke-linecap:round;stroke-linejoin:miter;"/>
                </g>
                <g transform="matrix(3.11111,0,0,3.11111,-76.8889,-63.5556)">
                  <circle cx="36.5" cy="26" r="1.5" style="fill:white;stroke:white;stroke-width:0.33px;stroke-miterlimit:2;"/>
                </g>
              </g>
            </svg>
            <span>Due to recent BS9991:2024 legislative updates, mechanical shafts require a minimum dimension of 600mm in any direction.</span>
          </p>
          <p><strong>If you'd like to futureproof your design, consider amending the shaft size.</strong></p>
        `
        )
      }
    ]
  },
  {
    title: 'How many doors?',
    text: `Please enter the total number of apartment doors per floor. If you're not sure of the exact number, an estimate is fine.`,
    video: 'https://a.storyblok.com/f/79904/x/19034b512a/number-of-doors-compressed.m4v',
    type: 'range',
    options: {
      min: 0,
      max: 25,
      step: 1
    },
    key: 'doors',
    button: 'Next',
    tip: {
      title: 'Why we need the number of apartment doors.',
      text: 'We need to know the amount of air leakage in the corridor and apartment doors add significant leakage.'
    },
    image: DoorsIcon,
    required: 1
  },
  {
    title: 'How many lift doors?',
    text: `Please enter the total number of lift doors per floor. If you're not sure of an exact number, an estimate is fine.`,
    video: 'https://a.storyblok.com/f/79904/x/6e2d328108/lift-doors-compressed.m4v',
    type: 'range',
    options: {
      min: 0,
      max: 5,
      step: 1
    },
    key: 'liftDoors',
    button: 'Next',
    tip: {
      title: 'Why we need the number of lift doors.',
      text: 'We need to know the amount of air leakage in the corridor and lift doors add significant leakage.'
    },
    image: LiftDoorsIcon
  },
  {
    title: 'Storeys',
    text: 'Enter the number of storeys in the building.',
    video: 'https://a.storyblok.com/f/79904/x/9c036239d5/number-of-storeys-compressed.m4v',
    type: 'input',
    key: 'storeys',
    button: 'Next',
    tip: {
      title: 'Why we need the number of storeys.',
      text: 'The number of storeys allows us to estimate the height of the smoke shaft.'
    },
    inputs: [
      {
        key: 'storeys',
        type: 'number',
        placeholder: 'Number of Storeys',
        required: true
      }
    ]
  },
  {
    title: 'Vertical Pod',
    text: 'Will you be using a vertical pod?',
    type: 'selectCard',
    key: 'verticalPod',
    options: [
      {
        title: 'Yes',
        image: VerticalPodIcon,
        text: '',
        value: true
      },
      {
        title: 'No',
        image: HorizontalPodIcon,
        text: '',
        value: false
      }
    ]
  },
  {
    title: 'Choose your features',
    subtitle: `Please select all that apply.`,
    type: 'features',
    key: 'features',
    button: 'Get specification',
    actions: {
      preselected: (data) => {
        // If vertical pod is selected, then return these defaults
        if (data.verticalPod) return ['Duty Fan', 'Standby Fan', 'Protective Enclosure', 'Lifting Eyes','Powered Louvre']
        // Otherwise, fall back to these horizontal defaults
        return ['Duty Fan', 'Standby Fan', 'Extreme Weather Protection', 'Lifting Eyes', 'Adjustable Feet']
      }
    }
  } as FormStep
]
